import { Area } from '@Domain/model/area';
import { Provider } from '@Domain/model/provider';
import { InsuranceType } from '@Framework/constants/InsuranceType';
import { Box, createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { CORPORATE_COLORS } from '@Ui/theme/colors/corporate';
import React from 'react';

import trustpilotStarsOne from '../../assets/logo/trustpilot/logo-trustpilot-1-0-stars.svg';
import trustpilotStarsOneAndHalf from '../../assets/logo/trustpilot/logo-trustpilot-1-5-stars.svg';
import trustpilotStarsTwo from '../../assets/logo/trustpilot/logo-trustpilot-2-0-stars.svg';
import trustpilotStarsTwoAndHalf from '../../assets/logo/trustpilot/logo-trustpilot-2-5-stars.svg';
import trustpilotStarsThree from '../../assets/logo/trustpilot/logo-trustpilot-3-0-stars.svg';
import trustpilotStarsThreeAndHalf from '../../assets/logo/trustpilot/logo-trustpilot-3-5-stars.svg';
import trustpilotStarsFour from '../../assets/logo/trustpilot/logo-trustpilot-4-0-stars.svg';
import trustpilotStarsFourAndHalf from '../../assets/logo/trustpilot/logo-trustpilot-4-5-stars.svg';
import trustpilotStarsFive from '../../assets/logo/trustpilot/logo-trustpilot-5-0-stars.svg';
import { Badge } from '../../atoms/Badge/Badge';
import { Link } from '../../atoms/Link';
import { MedicationInfo } from '../MedicationInfo/MedicationInfo';
import { ProviderModal } from '../ProviderModal/ProviderModal';

/**
 * ProviderTileProps
 */
export interface ProviderTileProps {
  provider: Provider;
  area: Area;
  children?: React.ReactNode;
  insuranceType?: InsuranceType;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderRadius: '0.5rem',
      padding: theme.spacing(1.75),
      border: '0.1rem solid',
      borderColor: CORPORATE_COLORS.BACKGROUND_LIGHT_PRIMARY_HIGH,
      backgroundColor: 'white'
    },
    MuiRating: {
      color: CORPORATE_COLORS.GREEN
    },
    border: {
      position: 'absolute',
      top: '4px',
      backgroundColor: 'white',
      height: '26px',
      padding: theme.spacing(0.5, 2),
      border: '1px solid',
      borderColor: CORPORATE_COLORS.BACKGROUND_LIGHT_PRIMARY_HIGH,
      borderRadius: '48px',
      color: CORPORATE_COLORS.CONTENT_DARK_PRIMARY_LOW,
      fontSize: '12px'
    },
    providersBenefitContainer: {
      marginTop: theme.spacing(1.25),
      marginBottom: theme.spacing(1.25)
    },
    trustPilotContainer: {
      fontSize: '12px',
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1)
    },
    trustpilotRating: {
      maxHeight: '20px'
    }
  })
);

const ProviderTile = (props: ProviderTileProps): React.ReactElement => {
  const { area, provider, insuranceType, children } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const gridFlex = isPhone ? 'flex-start' : 'flex-end';
  const rating = provider.rating;
  const modalTrigger = <Link data-qa-id="weitere-informationen-link" text="Weitere Informationen" />;

  let trustpilotStars;
  switch (true) {
    case rating >= 4.75 && rating <= 5:
      trustpilotStars = trustpilotStarsFive;
      break;
    case rating >= 4.25 && rating < 4.75:
      trustpilotStars = trustpilotStarsFourAndHalf;
      break;
    case rating >= 3.75 && rating < 4.25:
      trustpilotStars = trustpilotStarsFour;
      break;
    case rating >= 3.25 && rating < 3.75:
      trustpilotStars = trustpilotStarsThreeAndHalf;
      break;
    case rating >= 2.75 && rating < 3.25:
      trustpilotStars = trustpilotStarsThree;
      break;
    case rating >= 2.25 && rating < 2.75:
      trustpilotStars = trustpilotStarsTwoAndHalf;
      break;
    case rating >= 1.75 && rating < 2.25:
      trustpilotStars = trustpilotStarsTwo;
      break;
    case rating >= 1.25 && rating < 1.75:
      trustpilotStars = trustpilotStarsOneAndHalf;
      break;
    case rating < 1.25:
      trustpilotStars = trustpilotStarsOne;
      break;
  }

  const providerBenefits = (provider.benefits?.[props.area]?.[insuranceType || InsuranceType.PRIVATE] || []).map(
    ({ label }, id) => {
      const bonusLabel = 'Privatrezept-Bonus';
      return <Badge key={id} benefits isOutlined={label === bonusLabel} label={label} />;
    }
  );

  return (
    <Box className={classes.root}>
      {provider.isPremiumpartner === true && <div className={classes.border}>SHOP APOTHEKE Partner</div>}
      <Box pt={1}>
        <MedicationInfo {...provider} insuranceType={insuranceType} area={area} />
        <Box className={classes.providersBenefitContainer}>{providerBenefits}</Box>

        <Grid container>
          <Grid container item xs={12} sm={6}>
            <Box className={classes.trustPilotContainer}>
              {trustpilotStars && <img className={classes.trustpilotRating} src={trustpilotStars} />}
              <Box>Trustpilot:</Box>
              <Box fontWeight="fontWeightBold">{provider.rating}</Box>
            </Box>
          </Grid>
          <Grid container justifyContent={gridFlex} item xs={12} sm={6}>
            <Box pt={0.5}>
              <ProviderModal
                trigger={modalTrigger}
                provider={provider}
                area={area}
                insuranceType={insuranceType || InsuranceType.PRIVATE}
              />
            </Box>
          </Grid>
        </Grid>

        {children && <Box pt={2.5}>{children}</Box>}
      </Box>
    </Box>
  );
};

export { ProviderTile };
